import { useCallback, useMemo, useState } from 'react';
import constate from '../../common/lib/constate';

function useZoom() {
  const [isZoomOpened, setIsZoomOpened] = useState(false);

  const [zooms, setZooms] = useState([]);
  const [initialZooms, setInitialZooms] = useState([]);

  const [selectedZoom, setSelectedZoom] = useState({});
  const [zoomsSeekValue, setZoomsSeekValue] = useState(0);

  const [zoomProgress, setZoomProgress] = useState({ percents: 0, milliseconds: 0 });

  const [isZoomsChanged, setIsZoomsChanged] = useState(false);

  const enableZoom = useCallback(() => {
    setIsZoomOpened(true);
  }, []);

  const disableZoom = useCallback(() => {
    setIsZoomOpened(false);
  }, []);

  const updateInitialZooms = useCallback(zoomPoints => {
    setInitialZooms(zoomPoints);
  }, []);

  // add new zoom
  const updateZooms = useCallback(zoomPoints => {
    setZooms(zoomPoints);
  }, []);

  // change existing zoom
  const changeZooms = useCallback((id, changes) => {
    setZooms(prevState =>
      prevState.map(zoom => {
        if (zoom.id === id) {
          return { ...zoom, ...changes };
        }

        return zoom;
      })
    );
  }, []);

  const changeSelectedZoom = useCallback(zoom => {
    setSelectedZoom(zoom);
  }, []);

  const addNewZoom = useCallback(newZoom => {
    setZooms(prevState => [...prevState, newZoom].sort((a, b) => a.offset - b.offset));
    changeSelectedZoom(newZoom);
  }, []);

  const removeZoom = useCallback(zoomId => {
    setZooms(prevState => {
      const currentIndex = prevState.map(({ id }) => id).indexOf(zoomId);
      const filteredZooms = prevState.filter(zoom => zoom.id !== zoomId);

      if (filteredZooms[currentIndex]) {
        changeSelectedZoom(filteredZooms[currentIndex]);
      } else if (filteredZooms[0]) {
        changeSelectedZoom(filteredZooms[0]);
      } else {
        changeSelectedZoom({});
      }

      return filteredZooms;
    });
  }, []);

  const updateZoomProgress = useCallback(value => {
    setZoomProgress(value);
  }, []);

  const updateZoomsSeekValue = useCallback(state => {
    if (typeof state === 'number') setZoomsSeekValue(state);
  }, []);

  const zoomChanged = useCallback(value => {
    setIsZoomsChanged(value);
  }, []);

  return useMemo(
    () => ({
      isZoomOpened,
      zooms,
      initialZooms,
      zoomProgress,
      zoomsSeekValue,
      selectedZoom,
      isZoomsChanged,
      enableZoom,
      disableZoom,
      updateInitialZooms,
      updateZooms,
      changeZooms,
      changeSelectedZoom,
      addNewZoom,
      removeZoom,
      updateZoomProgress,
      updateZoomsSeekValue,
      zoomChanged,
    }),
    [
      isZoomOpened,
      zooms,
      initialZooms,
      zoomProgress,
      zoomsSeekValue,
      selectedZoom,
      isZoomsChanged,
      enableZoom,
      disableZoom,
      updateInitialZooms,
      updateZooms,
      changeZooms,
      removeZoom,
      changeSelectedZoom,
      addNewZoom,
      updateZoomProgress,
      updateZoomsSeekValue,
      zoomChanged,
    ]
  );
}

export const useZoomContext = constate(useZoom);

const ZoomContext = {
  Provider: useZoomContext.Provider,
};

export default ZoomContext;
